import { startRegistration } from '@simplewebauthn/browser';
import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useSession } from '../session';

/**
 * First sign-in: set up an authenticator app and a passkey. Both are required. The passkey is the one that
 * survives losing a phone, so it is not optional.
 */
export function Enrol() {
  const { me, refresh } = useSession();
  const navigate = useNavigate();
  const [secret, setSecret] = useState<{ secret: string; uri: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [passkeyName, setPasskeyName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const totpDone = me?.factors.totp ?? false;
  const passkeyDone = (me?.factors.passkeys.length ?? 0) > 0;

  useEffect(() => {
    if (totpDone || secret) return;
    api<{ secret: string; uri: string }>('/api/auth/totp/enrol/start', { method: 'POST' })
      .then(async (s) => setSecret({ ...s, qr: await QRCode.toDataURL(s.uri, { margin: 1, width: 192 }) }))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not start authenticator setup'));
  }, [totpDone, secret]);

  async function confirmTotp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/totp/enrol/confirm', { method: 'POST', body: { code } });
      setCode('');
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The code was not accepted');
    } finally {
      setBusy(false);
    }
  }

  async function addPasskey() {
    setBusy(true);
    setError(null);
    try {
      const optionsJSON = await api<Parameters<typeof startRegistration>[0]['optionsJSON']>('/api/auth/passkeys/register/options', { method: 'POST' });
      const response = await startRegistration({ optionsJSON });
      await api('/api/auth/passkeys/register/verify', { method: 'POST', body: { response, name: passkeyName || undefined } });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'The passkey was not created');
    } finally {
      setBusy(false);
    }
  }

  if (!me) return null;

  // Wider than the other sign-in cards, so the QR code and the instructions beside it fit on one row next
  // to the step number rather than stacking.
  return (
    <div className="card narrow" style={{ maxWidth: 560 }}>
      <h1>Set up your sign-in</h1>
      <p className="lede">Two steps, once. An authenticator app for codes, and a passkey that survives losing your phone.</p>
      {error && <div className="alert">{error}</div>}
      <div className="steps">
        <div className={`step ${totpDone ? 'done' : ''}`}>
          <div className="n">{totpDone ? '✓' : '1'}</div>
          <div>
            <h2 style={{ margin: '2px 0 8px' }}>Authenticator app</h2>
            {totpDone ? (
              <p className="muted">Done.</p>
            ) : secret ? (
              <form onSubmit={confirmTotp}>
                <div className="qr">
                  <img src={secret.qr} width={192} height={192} alt="QR code for your authenticator app" />
                  <div className="small">
                    <p>Scan with Google Authenticator, Microsoft Authenticator, 1Password or similar.</p>
                    <p className="muted">Or enter the key manually:<br /><code>{secret.secret}</code></p>
                  </div>
                </div>
                <label>
                  Enter the six-digit code it shows
                  <input id="enrol-code" inputMode="numeric" pattern="[0-9]*" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
                </label>
                <button type="submit" className="block" disabled={busy || code.length !== 6}>Confirm authenticator</button>
              </form>
            ) : (
              <p className="muted">Preparing…</p>
            )}
          </div>
        </div>

        <div className={`step ${passkeyDone ? 'done' : ''}`}>
          <div className="n">{passkeyDone ? '✓' : '2'}</div>
          <div>
            <h2 style={{ margin: '2px 0 8px' }}>Passkey</h2>
            {passkeyDone ? (
              <p className="muted">Done. You can add more from your account page.</p>
            ) : (
              <>
                <p className="small muted">Use your phone, laptop or a security key. A passkey synced through iCloud Keychain or Google Password Manager is the one that gets you back in if you lose a device.</p>
                <div className="row">
                  <input id="passkey-name" placeholder="Name, e.g. Work laptop" value={passkeyName} onChange={(e) => setPasskeyName(e.target.value)} />
                  <button type="button" onClick={addPasskey} disabled={busy || !totpDone}>Create passkey</button>
                </div>
                {!totpDone && <p className="small muted">Finish step 1 first.</p>}
              </>
            )}
          </div>
        </div>
      </div>
      {/* Each step's own button is the thing to press while that step is open; this one only wakes once
          both are done, so there is never more than one live primary on the page. */}
      <button
        type="button"
        className="block"
        style={{ marginTop: 24 }}
        disabled={!(totpDone && passkeyDone)}
        onClick={() => refresh().then(() => navigate('/'))}
      >
        Finish
      </button>
    </div>
  );
}
